import type { Owned } from '@tunasong/models'
import { profilesApi, skipToken, useSelector } from '@tunasong/redux'

const noEmails: Record<string, string> = {}

/**
 * The emails of the people an entity is shared with (userId → email), for its owner and the people it is shared with
 * by name. Public profiles carry no email and some people have no name, so members identify each other by email.
 * Reading the entity through a public or logged-in grant gets none.
 */
export const useMemberEmails = (entity?: Owned<{ id?: string }> | null) => {
  const userId = useSelector(state => state.user.userId)
  const entityId = entity?.id
  const isMember = Boolean(
    entityId && userId && (entity?.userId === userId || entity?.acls?.some(acl => acl.principal === userId))
  )
  const memberKey = entity ? [entity.userId, ...(entity.acls ?? []).map(acl => acl.principal)].sort().join(',') : ''
  const { data } = profilesApi.useLoadMemberEmailsQuery(entityId && isMember ? { entityId, memberKey } : skipToken)
  return data?.emails ?? noEmails
}

/** How a person reads in member lists: their name, else the email a member may see, else a neutral label */
export const memberLabel = (
  profile: { userId: string; name?: string; email?: string } | undefined,
  emails: Record<string, string>
) => (profile ? (profile.name ?? emails[profile.userId] ?? profile.email ?? 'Unnamed user') : undefined)
